import { Injectable } from '@angular/core';
import { SplashScreenConfig } from 'src/app/config/config.model';

type SplashPhase = 'idle' | 'auth' | 'dashboard';

@Injectable({ providedIn: 'root' })
export class SplashscreenService {
    private static readonly STYLE_ID = 'eic-dashboard-splashscreen-style';
    private static readonly OVERLAY_ID = 'eic-dashboard-splashscreen-overlay';

    private phase: SplashPhase = 'idle';
    private enabledForNewDashboard = false;
    private hasFirstWidgetLoaded = false;
    private fadeTimerId: number | null = null;
    private styleInjected = false;

    private config: SplashScreenConfig = {
        enabled: false,
        showDuringAuthCheck: true,
        showDuringDashboardInit: true,
        showProgressIndicator: true,
        fadeOutMs: 350,
        imageUrl: '',
        customHtml: '',
        statusMessages: {
            checkingLoginStatus: 'Checking login status...',
            settingThingsUp: 'Setting things up...',
            loadingDashboardData: 'Loading dashboard data...',
            ready: 'Ready'
        }
    };

    configure(config: SplashScreenConfig | null | undefined, useNewDashboard: boolean): void {
        this.config = {
            ...this.config,
            ...(config || {}),
            statusMessages: {
                ...this.config.statusMessages,
                ...(config?.statusMessages || {})
            }
        };

        this.enabledForNewDashboard = !!useNewDashboard && !!this.config.enabled;

        if (!this.enabledForNewDashboard) {
            this.hideImmediately();
            return;
        }

        this.ensureDom();
        this.applyCustomContent();
    }

    startAuthCheck(): void {
        if (!this.enabledForNewDashboard || !this.config.showDuringAuthCheck) {
            return;
        }

        this.phase = 'auth';
        this.hasFirstWidgetLoaded = false;
        this.show(this.config.statusMessages.checkingLoginStatus);
    }

    startDashboardInit(): void {
        if (!this.enabledForNewDashboard || !this.config.showDuringDashboardInit) {
            return;
        }

        this.phase = 'dashboard';
        this.hasFirstWidgetLoaded = false;
        this.show(this.config.statusMessages.settingThingsUp);
    }

    markDashboardShellReady(): void {
        if (this.phase !== 'dashboard') {
            return;
        }

        this.setStatus(this.config.statusMessages.loadingDashboardData);
    }

    notifyFirstWidgetDataLoaded(): void {
        if (this.phase !== 'dashboard' || this.hasFirstWidgetLoaded) {
            return;
        }

        this.hasFirstWidgetLoaded = true;
        this.setStatus(this.config.statusMessages.ready);
        this.fadeOutAndHide();
    }

    hideImmediately(): void {
        this.clearFadeTimer();
        this.phase = 'idle';
        this.hasFirstWidgetLoaded = false;

        const overlay = this.overlay();
        if (!overlay) {
            return;
        }

        overlay.classList.remove('eic-splashscreen-overlay--visible', 'eic-splashscreen-overlay--fadeout');
        overlay.setAttribute('aria-hidden', 'true');
    }

    private show(statusText: string): void {
        this.ensureDom();
        this.applyCustomContent();

        const overlay = this.overlay();
        if (!overlay) {
            return;
        }

        this.clearFadeTimer();
        overlay.classList.remove('eic-splashscreen-overlay--fadeout');
        overlay.classList.add('eic-splashscreen-overlay--visible');
        overlay.setAttribute('aria-hidden', 'false');
        this.setStatus(statusText);
    }

    private setStatus(statusText: string): void {
        const statusElement = document.getElementById('eic-splashscreen-status');
        if (!statusElement) {
            return;
        }

        statusElement.textContent = statusText;
    }

    private fadeOutAndHide(): void {
        const overlay = this.overlay();
        if (!overlay) {
            this.phase = 'idle';
            return;
        }

        overlay.classList.add('eic-splashscreen-overlay--fadeout');
        this.clearFadeTimer();
        this.fadeTimerId = window.setTimeout(() => {
            overlay.classList.remove('eic-splashscreen-overlay--visible', 'eic-splashscreen-overlay--fadeout');
            overlay.setAttribute('aria-hidden', 'true');
            this.phase = 'idle';
            this.fadeTimerId = null;
        }, this.config.fadeOutMs);
    }

    private applyCustomContent(): void {
        const image = document.getElementById('eic-splashscreen-image') as HTMLImageElement | null;
        if (image) {
            const hasImage = typeof this.config.imageUrl === 'string' && this.config.imageUrl.trim() !== '';
            if (hasImage) {
                image.src = this.config.imageUrl!;
                image.classList.add('eic-splashscreen-image--visible');
            } else {
                image.removeAttribute('src');
                image.classList.remove('eic-splashscreen-image--visible');
            }
        }

        const custom = document.getElementById('eic-splashscreen-custom');
        if (custom) {
            custom.innerHTML = this.config.customHtml || '';
        }

        const spinner = document.getElementById('eic-splashscreen-spinner');
        const progressBar = document.getElementById('eic-splashscreen-progressbar');
        if (spinner) {
            if (this.config.showProgressIndicator) {
                spinner.classList.add('eic-splashscreen-spinner--visible');
            } else {
                spinner.classList.remove('eic-splashscreen-spinner--visible');
            }
        }

        if (progressBar) {
            if (this.config.showProgressIndicator) {
                progressBar.classList.add('eic-splashscreen-progressbar--visible');
            } else {
                progressBar.classList.remove('eic-splashscreen-progressbar--visible');
            }
        }
    }

    private ensureDom(): void {
        if (typeof document === 'undefined') {
            return;
        }

        this.ensureStyle();

        if (this.overlay()) {
            return;
        }

        const overlay = document.createElement('div');
        overlay.id = SplashscreenService.OVERLAY_ID;
        overlay.className = 'eic-splashscreen-overlay';
        overlay.setAttribute('role', 'status');
        overlay.setAttribute('aria-live', 'polite');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.innerHTML = `
      <div class="eic-splashscreen-panel">
        <img id="eic-splashscreen-image" class="eic-splashscreen-image" alt="Splash image" />
        <div id="eic-splashscreen-custom" class="eic-splashscreen-custom"></div>
        <div class="eic-splashscreen-progress-row">
          <span id="eic-splashscreen-spinner" class="eic-splashscreen-spinner"></span>
          <span id="eic-splashscreen-status" class="eic-splashscreen-status"></span>
        </div>
        <div id="eic-splashscreen-progressbar" class="eic-splashscreen-progressbar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuetext="Loading">
          <span class="eic-splashscreen-progressbar-indeterminate"></span>
        </div>
      </div>
    `;

        document.body.appendChild(overlay);
    }

    private ensureStyle(): void {
        if (this.styleInjected || document.getElementById(SplashscreenService.STYLE_ID)) {
            this.styleInjected = true;
            return;
        }

        const style = document.createElement('style');
        style.id = SplashscreenService.STYLE_ID;
        style.textContent = `
      .eic-splashscreen-overlay {
        position: fixed;
        inset: 0;
        z-index: 3000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 350ms ease;
        background: var(--mat-sys-surface-container, var(--sys-surface-container));
      }

      .eic-splashscreen-overlay--visible {
        opacity: 1;
        pointer-events: auto;
      }

      .eic-splashscreen-overlay--fadeout {
        opacity: 0;
      }

      .eic-splashscreen-panel {
        width: min(560px, 100%);
        max-height: min(88vh, 680px);
        overflow: auto;
        border-radius: 12px;
        padding: 20px;
        box-sizing: border-box;
        background: var(--mat-sys-surface, var(--sys-surface));
        color: var(--mat-sys-on-surface, var(--sys-on-surface));
        border: 1px solid var(--mat-sys-outline-variant, var(--sys-outline-variant));
      }

      .eic-splashscreen-image {
        width: 100%;
        max-height: 240px;
        object-fit: contain;
        margin-bottom: 12px;
        display: none;
      }

      .eic-splashscreen-image--visible {
        display: block;
      }

      .eic-splashscreen-custom {
        width: 100%;
        margin-bottom: 14px;
      }

      .eic-splashscreen-progress-row {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 28px;
      }

      .eic-splashscreen-spinner {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid var(--mat-sys-outline-variant, var(--sys-outline-variant));
        border-top-color: var(--mat-sys-primary, var(--sys-primary));
        animation: eic-splashscreen-spin 1s linear infinite;
        display: none;
        flex-shrink: 0;
      }

      .eic-splashscreen-spinner--visible {
        display: inline-block;
      }

      .eic-splashscreen-status {
        font-size: 14px;
        line-height: 20px;
        word-break: break-word;
      }

      .eic-splashscreen-progressbar {
        margin-top: 12px;
        width: 100%;
        height: 8px;
        border-radius: 999px;
        overflow: hidden;
        background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
        border: 1px solid var(--mat-sys-outline-variant, var(--sys-outline-variant));
        display: none;
      }

      .eic-splashscreen-progressbar--visible {
        display: block;
      }

      .eic-splashscreen-progressbar-indeterminate {
        display: block;
        width: 45%;
        height: 100%;
        border-radius: inherit;
        background: var(--mat-sys-primary, var(--sys-primary));
        animation: eic-splashscreen-progress-slide 1.2s ease-in-out infinite;
      }

      @keyframes eic-splashscreen-spin {
        to { transform: rotate(360deg); }
      }

      @keyframes eic-splashscreen-progress-slide {
        0% { transform: translateX(-120%); }
        100% { transform: translateX(250%); }
      }

      @media (max-width: 768px) {
        .eic-splashscreen-overlay {
          padding: 12px;
        }

        .eic-splashscreen-panel {
          border-radius: 10px;
          padding: 14px;
          width: 100%;
          max-height: 92vh;
        }

        .eic-splashscreen-image {
          max-height: 180px;
        }

        .eic-splashscreen-status {
          font-size: 13px;
          line-height: 18px;
        }
      }
    `;

        document.head.appendChild(style);
        this.styleInjected = true;
    }

    private overlay(): HTMLElement | null {
        return document.getElementById(SplashscreenService.OVERLAY_ID);
    }

    private clearFadeTimer(): void {
        if (this.fadeTimerId !== null) {
            window.clearTimeout(this.fadeTimerId);
            this.fadeTimerId = null;
        }
    }
}
